(() => {
  if (customElements.get('pinus-bundle-price')) return;

  // Read Kaching's rendered totals, never recalculate its discount rules.
  function moneyToCents(text, format) {
    const match = text.match(/\d[\d\s.,'’]*/);
    if (!match) return null;
    let amount = match[0].replace(/[\s'’]/g, '');
    if (format.includes('no_decimals')) {
      amount = amount.replace(/[.,]/g, '');
    } else if (format.includes('comma_separator')) {
      amount = amount.replace(/\./g, '').replace(',', '.');
    } else {
      amount = amount.replace(/,/g, '');
    }
    const value = Number(amount);
    return Number.isFinite(value) ? Math.round(value * 100) : null;
  }

  class PinusBundlePrice extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('.product__info-container');
      if (!this.scope) return;
      this.nativePrice = this.querySelector('[data-native-price]');
      this.total = this.querySelector('[data-bundle-total]');
      this.current = this.querySelector('[data-bundle-current]');
      this.compare = this.querySelector('[data-bundle-compare]');
      this.original = this.querySelector('[data-bundle-original]');
      this.savings = this.querySelector('[data-bundle-savings]');
      this.application = this.querySelector('[data-bundle-application]');
      this.applicationWrapper = this.querySelector('[data-bundle-application-wrapper]');
      this.money = new Intl.NumberFormat(this.dataset.locale || 'de', {
        style: 'currency', currency: this.dataset.currency || 'EUR'
      });
      this.currency = this.dataset.currency || 'EUR';
      this.currencySymbol = this.money.formatToParts(0).find((part) => part.type === 'currency').value;
      this.percent = new Intl.NumberFormat(this.dataset.locale || 'de', {
        maximumFractionDigits: 1
      });
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      };
      // Includes asynchronous widget load, rerenders and variant price updates.
      // Ignore our own output to avoid an observer feedback loop.
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(this.scope, {
        subtree: true, childList: true, characterData: true,
        attributes: true, attributeFilter: ['class', 'checked']
      });
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.bindWidget(null);
      this.frame = null;
      this.signature = null;
    }

    bindWidget(widget) {
      if (widget === this.widget) return;
      for (const event of ['deal-bar-selected', 'variant-selected', 'variants-changed']) {
        this.widget?.removeEventListener(event, this.schedule);
        widget?.addEventListener(event, this.schedule);
      }
      this.widget = widget;
    }

    formatMoney(cents) {
      const number = new Intl.NumberFormat(this.dataset.locale || 'de', {
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
      return `${this.currencySymbol} ${number} ${this.currency}`;
    }

    applicationPrice(price, bar) {
      const volume = Number(this.dataset.volumeMl);
      const dropsPerMl = Number(this.dataset.dropsPerMl);
      const dropsPerUse = Number(this.dataset.dropsPerUse);
      if (![volume, dropsPerMl, dropsPerUse].every((value) => Number.isFinite(value) && value > 0) || !bar) return null;
      try {
        const config = JSON.parse(this.widget.getAttribute('deal-block') || '{}');
        const deal = config.dealBars?.find((item) => item.id === bar.getAttribute('data-deal-bar-id'));
        // Count paid bottles, excluding free gifts. Do not guess a dynamic quantity.
        const quantity = Number(deal?.quantity);
        if (!Number.isFinite(quantity) || quantity <= 0 || deal.quantitySelector) return null;
        return price * dropsPerUse / (quantity * volume * dropsPerMl);
      } catch {
        return null;
      }
    }

    sync() {
      this.bindWidget(this.scope.querySelector('kaching-bundles-block'));
      const bar = this.widget?.querySelector('.kaching-bundles__bar--selected');
      const priceText = bar?.querySelector('.kaching-bundles__bar-price')?.textContent.trim() || '';
      const compareText = bar?.querySelector('.kaching-bundles__bar-full-price')?.textContent.trim() || '';
      const format = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';
      const price = moneyToCents(priceText, format);
      const fullPrice = moneyToCents(compareText, format);
      const perApplication = price !== null ? this.applicationPrice(price, bar) : null;
      const signature = JSON.stringify([priceText, compareText, price, fullPrice, perApplication]);
      if (signature === this.signature) return;
      this.signature = signature;
      if (!bar || price === null) {
        this.nativePrice.hidden = false;
        this.total.hidden = true;
        return;
      }
      const saved = fullPrice !== null ? Math.max(0, fullPrice - price) : 0;
      this.current.textContent = this.formatMoney(price);
      this.original.textContent = fullPrice !== null ? this.formatMoney(fullPrice) : '';
      this.compare.hidden = saved <= 0;
      this.savings.hidden = saved <= 0;
      this.savings.textContent = saved > 0
        ? `${this.dataset.savingsLabel} ${this.formatMoney(saved)} (${this.percent.format(saved / fullPrice * 100)} %)`
        : '';
      if (this.application && this.applicationWrapper) {
        this.applicationWrapper.hidden = perApplication === null;
        this.application.textContent = perApplication !== null
          ? this.dataset.applicationLabel.replace('[price]', this.formatMoney(Math.round(perApplication)))
          : '';
      }
      this.nativePrice.hidden = true;
      this.total.hidden = false;
    }
  }

  customElements.define('pinus-bundle-price', PinusBundlePrice);
})();
